
import { useEffect, useState } from 'react';
import { projectMembersCache } from '@/lib/services/project-members-cache.service';
import { useOrganizationStore } from '@/store/organization.store';
import { createLogger } from '@/lib/logger';

const logger = createLogger('hooks/useProjectMembers');

interface UseProjectMembersOptions {
  enabled?: boolean;
}

/**
 * Hook to get members for a project with caching
 */
export function useProjectMembers(
  projectId: string | undefined,
  options: UseProjectMembersOptions = {}
) {
  const { enabled = true } = options;
  const { currentOrganization } = useOrganizationStore();
  const [members, setMembers] = useState<Array<{
    id: string;
    userId?: string | null;
    memberId?: string | null;
    name: string;
    email: string;
    avatarUrl?: string;
  }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Skip if disabled or missing required data
    if (!enabled || !currentOrganization?.id) {
      setMembers([]);
      return;
    }

    let canceled = false;
    setIsLoading(true);
    setError(null);

    projectMembersCache.getAssignableUsers(projectId || '', currentOrganization.id)
      .then(fetchedMembers => {
        if (!canceled) {
          setMembers(fetchedMembers);
          setError(null);
        }
      })
      .catch(err => {
        if (!canceled) {
          logger.error('[useProjectMembers] Error fetching members:', err);
          setError(err);
          setMembers([]);
        }
      })
      .finally(() => {
        if (!canceled) {
          setIsLoading(false);
        }
      });

    return () => {
      canceled = true;
    };
  }, [projectId, currentOrganization?.id, enabled]);

  return {
    members,
    isLoading,
    error,
    invalidate: () => {
      if (currentOrganization?.id) {
        projectMembersCache.invalidate(currentOrganization.id, projectId);
      }
    }
  };
}
